import type { LuggageType, LuggageTypeRegistry } from './engine/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { shouldShowPetTransferInfo } from '@/scopes/account/booking-details/domain/policies/shouldShowPetTransferInfo'
import { useBookingDetailsActions } from '@/scopes/account/booking-details/infrastructure/dependencies/dependencies'
import { useBookingDetailsStore } from '@/scopes/account/booking-details/infrastructure/useBookingDetailsStore'
import {
  createMedicalEquipmentEntry,
} from '@/scopes/account/booking-details/pages/components/SpecialLuggageSheet/form-schemas/medical-equipment'
import { getActiveParticipantOptions } from '@/scopes/account/booking-details/pages/selectors'
import { formatParticipantFullName } from '@/scopes/account/booking-details/pages/utils/participantName'
import { DropdownInput } from '@/scopes/common/components/FormElements/DropdownInput'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { translateAccommodationSpecialRequirements } from './accommodationSpecialRequirements'
import { SchemaRenderer } from './engine/SchemaRenderer'
import { createBikeEntry } from './form-schemas/bike'
import { createDeltaKiteEntry } from './form-schemas/delta-kite'
import { createDiveEquipmentEntry } from './form-schemas/dive-equipment'
import { createFishEquipmentEntry } from './form-schemas/fish-equipment'
import { createGolfEquipmentEntry } from './form-schemas/golf-equipment'
import { createKiteEntry } from './form-schemas/kite'
import { createMusicInstrumentEntry } from './form-schemas/music-instrument'
import { createPetEntry } from './form-schemas/pet'
import { createSurfboardEntry } from './form-schemas/surfboard'
import { createTennisEquipmentEntry } from './form-schemas/tennis-equipment'
import styles from './SpecialLuggageForm.module.css'

const SUPPORTED_LUGGAGE_TYPES = new Set<LuggageType>(['bike', 'pet', 'delta-kite', 'kite', 'dive-equipment', 'fish-equipment', 'golf-equipment', 'music-instrument', 'surfboard', 'tennis-equipment', 'medical'])

function FormBody({ onCancel, isSubmitting }: { onCancel: () => void, isSubmitting: boolean }) {
  const { formatMessage } = useIntl()
  const participants = useBookingDetailsStore(getActiveParticipantOptions)
  const transportType = useBookingDetailsStore(state => state.transportType)
  const hasCarRental = useBookingDetailsStore(state => state.hasCarRental)
  const brandName = useConfiguration('brandName')
  const { myTransavia, flightInformation, medicalCertificate, medicineInfo } = useConfiguration('links')

  const isFlight = transportType === 'flight'

  const participantOptions = participants.map(({ value, fullName: { titleKey, firstName, lastName } }) => {
    const resolvedTitle = titleKey
      ? formatMessage({ id: titleKey })
      : null

    return {
      value,
      text: formatParticipantFullName(resolvedTitle, firstName, lastName),
    }
  })

  const petEntry = useMemo(
    () => createPetEntry({
      isFlight,
      hasCarRental,
      showTransferInfo: shouldShowPetTransferInfo(brandName),
      links: { termsAndConditions: flightInformation },
    }),
    [isFlight, hasCarRental, brandName, flightInformation],
  )

  const registry: LuggageTypeRegistry = useMemo(
    () => ({
      'bike': createBikeEntry({ links: { termsAndConditions: flightInformation } }),
      'pet': petEntry,
      'delta-kite': createDeltaKiteEntry({ links: { termsAndConditions: flightInformation } }),
      'kite': createKiteEntry({ links: { termsAndConditions: flightInformation } }),
      'dive-equipment': createDiveEquipmentEntry({ links: { termsAndConditions: flightInformation } }),
      'fish-equipment': createFishEquipmentEntry({ links: { termsAndConditions: flightInformation } }),
      'golf-equipment': createGolfEquipmentEntry({ links: { termsAndConditions: flightInformation } }),
      'music-instrument': createMusicInstrumentEntry({ links: { termsAndConditions: flightInformation } }),
      'surfboard': createSurfboardEntry({ links: { termsAndConditions: flightInformation } }),
      'tennis-equipment': createTennisEquipmentEntry({ links: { termsAndConditions: flightInformation } }),
      'medical': createMedicalEquipmentEntry({ links: {
        oxygenBanner: myTransavia,
        termsAndConditions: flightInformation,
        medicalCertificate,
        medicineInfo,
      } }),
    }),
    [petEntry, myTransavia, flightInformation, medicalCertificate, medicineInfo],
  )

  const luggageType = useWatch({ name: 'luggageType' }) as LuggageType | ''
  const entry = luggageType ? registry[luggageType] : undefined

  const { reset, getValues } = useFormContext()
  const [appliedLuggageType, setAppliedLuggageType] = React.useState('')

  useEffect(() => {
    if (entry) {
      reset({
        ...entry.schema.defaultValues,
        participant: getValues('participant'),
        luggageType,
      })
      setAppliedLuggageType(luggageType)
    }
    else {
      setAppliedLuggageType('')
    }
  }, [luggageType, entry, reset, getValues])

  const isSchemaReady = !!entry && appliedLuggageType === luggageType

  // Keep this order aligned with the most-booked luggage types (highest to lowest).
  const luggageTypeOptions = [
    { value: 'medical', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_medical' }) },
    { value: 'pet', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_pet' }) },
    { value: 'golf-equipment', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_golf_equipment' }) },
    { value: 'dive-equipment', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_dive_equipment' }) },
    { value: 'bike', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_bike' }) },
    { value: 'surfboard', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_surfboard' }) },
    { value: 'kite', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_kite' }) },
    { value: 'delta-kite', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_delta_kite' }) },
    { value: 'fish-equipment', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_fish_equipment' }) },
    { value: 'music-instrument', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_music_instrument' }) },
    { value: 'tennis-equipment', text: formatMessage({ id: 'booking_details_special_luggage_luggage_type_tennis_equipment' }) },
  ]

  return (
    <>
      <Typography as="p" variant="body-m-regular-loose">
        {formatMessage({ id: 'booking_details_special_luggage_sheet_introduction' })}
      </Typography>

      <DropdownInput
        name="participant"
        label={formatMessage({ id: 'booking_details_special_luggage_participant_label' })}
        items={participantOptions}
        required
        requiredErrorMessage={formatMessage({ id: 'booking_details_special_luggage_participant_required_error' })}
        placeholder={formatMessage({ id: 'booking_details_special_luggage_participant_placeholder' })}
      />

      <DropdownInput
        name="luggageType"
        label={formatMessage({ id: 'booking_details_special_luggage_luggage_type_label' })}
        items={luggageTypeOptions}
        required
        requiredErrorMessage={formatMessage({ id: 'booking_details_special_luggage_luggage_type_required_error' })}
        placeholder={formatMessage({ id: 'booking_details_special_luggage_luggage_type_placeholder' })}
      />

      {luggageType && !entry && (
        <Typography as="p" variant="body-s-regular-tight" className={styles.luggageTypeError}>
          {formatMessage({ id: 'booking_details_special_luggage_luggage_type_not_supported_error' })}
        </Typography>
      )}

      {isSchemaReady && <SchemaRenderer schema={entry.schema} />}

      <div className={styles.actions}>
        <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
          {formatMessage({ id: 'booking_details_special_luggage_submit_button' })}
        </Button>
        <Button variant="transparentNeutral" onClick={onCancel} type="button">
          {formatMessage({ id: 'common_cancel_button' })}
        </Button>
      </div>
    </>
  )
}

export function SpecialLuggageForm() {
  const { formatMessage } = useIntl()
  const { submitSpecialLuggage } = useBookingDetailsActions()
  const submitStatus = useBookingDetailsStore(state => state.specialLuggage.submitStatus)
  const isSubmitting = submitStatus === 'loading'
  const setIn = useBookingDetailsStore(state => state.setIn)

  function handleCancel() {
    setIn('specialLuggage.submitStatus', 'idle')
    setIn('specialLuggage.isOpen', false)
  }

  const methods = useForm<Record<string, unknown>>({
    shouldUnregister: true,
    mode: 'onBlur',
    defaultValues: { participant: '', luggageType: '' },
  })

  async function handleSubmit(values: Record<string, unknown>) {
    if (!SUPPORTED_LUGGAGE_TYPES.has(values.luggageType as LuggageType))
      return

    const submissionValues = values.luggageType === 'medical'
      ? {
          ...values,
          accommodationSpecialRequirements: translateAccommodationSpecialRequirements(
            values.accommodationSpecialRequirements as string[] | undefined,
            formatMessage,
          ),
        }
      : values

    await submitSpecialLuggage(submissionValues)
  }

  if (submitStatus === 'success') {
    return (
      <div className={styles.resultState}>
        <Typography as="h2" variant="display-g-regular">
          {formatMessage({ id: 'booking_details_special_luggage_success_title' })}
        </Typography>
        <Typography as="p" variant="body-m-regular-loose">
          {formatMessage({ id: 'booking_details_special_luggage_success_message' })}
        </Typography>
        <Button onClick={handleCancel}>
          {formatMessage({ id: 'close' })}
        </Button>
      </div>
    )
  }

  if (submitStatus === 'error') {
    return <ErrorBanner />
  }

  return (
    <FormProvider {...methods}>
      <div className={styles.formWrapper}>
        <form
          className={cn(styles.form, isSubmitting && styles.formDisabled)}
          onSubmit={methods.handleSubmit(handleSubmit)}
          noValidate
        >
          <FormBody onCancel={handleCancel} isSubmitting={isSubmitting} />
        </form>
        {isSubmitting && (
          <div className={styles.loaderOverlay}>
            <IndeterminateProgressCircle />
          </div>
        )}
      </div>
    </FormProvider>
  )
}

function ErrorBanner() {
  const { formatMessage } = useIntl()

  return (
    <Banner hasIcon color="danger" size="M">
      <Banner.TextsWrapper>
        <Banner.Text>
          {formatMessage({ id: 'common_error_title' })}
        </Banner.Text>

        <Banner.SupportingText innerHTML={formatMessage({ id: 'common_error_message' })} />
      </Banner.TextsWrapper>
    </Banner>
  )
}
